import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useT } from "@/context/LanguageContext";
import { getSEO, SITE_NAME, SITE_SUFFIX, DEFAULT_OG_IMAGE } from "@/lib/seo";

/**
 * useSEO — updates <title>, meta description, canonical, hreflang and OG
 * tags every time the route or language changes.
 *
 * Pass an override object to customise (e.g. product pages):
 *   useSEO({ title: p.name, description: p.short_description, image: p.images?.[0] })
 */
const OG_LOCALE = { en: "en_GB", pt: "pt_PT", es: "es_ES" };

/**
 * Turn a pathname into a stable slug so we can look up an editable
 * translation key like `seo.title.home` or `seo.title.collection.dresses`.
 * Returns null for pathnames that are not admin-editable via translations
 * (fall through to the static seo.js table).
 */
const pathnameToSlug = (pathname) => {
  if (pathname === "/") return "home";
  if (pathname === "/about") return "about";
  if (pathname === "/journal") return "journal";
  if (pathname === "/account") return "account";
  if (pathname === "/checkout/success") return "checkout.success";
  const m = pathname.match(/^\/collection\/([^/]+)$/);
  if (m) return `collection.${m[1]}`;
  return null;
};

/** If the translation returned the key itself (no entry + no override), treat as "no value". */
const tOrNull = (t, key) => {
  const v = t(key);
  return v && v !== key ? v : null;
};

const setMeta = (attr, value, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href, extra = {}) => {
  const selector = extra.hreflang
    ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (extra.hreflang) el.setAttribute("hreflang", extra.hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export function useSEO(override = {}) {
  const { pathname } = useLocation();
  const { lang, t } = useT();

  // The site name shown at the end of every browser tab title (e.g.
  // "About · GOYA LISBOA"). Admins can override it per language from
  // the Content editor (key: seo.siteName).
  const siteName = (t("seo.siteName") || SITE_NAME).trim();
  const suffix = siteName ? ` · ${siteName}` : "";

  useEffect(() => {
    const base = getSEO(pathname, lang);
    const slug = pathnameToSlug(pathname);
    // Prefer an admin-editable translation override (per-language) over the
    // hardcoded seo.js table. Product page overrides (passed via `override`)
    // still win over everything.
    const editableTitle = slug ? tOrNull(t, `seo.title.${slug}`) : null;
    const editableDesc = slug ? tOrNull(t, `seo.desc.${slug}`) : null;

    const baseTitle = editableTitle || base.title;
    const baseDesc = editableDesc || base.description;

    const rawTitle = override.title || baseTitle;
    const title = rawTitle.trim() === siteName ? rawTitle : `${rawTitle}${suffix}`;
    const description = override.description || baseDesc;
    const image = override.image || DEFAULT_OG_IMAGE;
    const noindex = override.noindex ?? base.noindex ?? false;

    // Origin-aware canonical (works on preview, production, custom domain)
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const canonical = `${origin}${pathname}`;

    document.title = title;
    document.documentElement.lang = lang === "pt" ? "pt-PT" : lang;

    setMeta("name", "description", description);
    if (base.keywords) setMeta("name", "keywords", base.keywords);
    setMeta(
      "name",
      "robots",
      noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1"
    );

    setLink("canonical", canonical);
    setLink("alternate", `${origin}${pathname}?lang=en`, { hreflang: "en" });
    setLink("alternate", `${origin}${pathname}?lang=pt`, { hreflang: "pt-PT" });
    setLink("alternate", `${origin}${pathname}?lang=es`, { hreflang: "es" });
    setLink("alternate", `${origin}${pathname}`, { hreflang: "x-default" });

    // Open Graph
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", image);
    setMeta("property", "og:site_name", siteName || SITE_NAME);
    setMeta("property", "og:type", override.ogType || "website");
    setMeta("property", "og:locale", OG_LOCALE[lang] || "en_GB");

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);
  }, [pathname, lang, siteName, suffix, t, override.title, override.description, override.image, override.noindex, override.ogType]);
}

/**
 * Convenience: inject a JSON-LD <script> tag (e.g. for Product pages).
 * `id` lets us safely replace an existing block when data changes.
 */
export function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return undefined;
    const existing = document.getElementById(id);
    const script = existing || document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.textContent = JSON.stringify(data);
    if (!existing) document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [id, data]);
}
